/**
 * Single source of truth for section-backdrop layer visibility toggles.
 * Used by the Layers troubleshooting pane and individual inspector sections.
 */

export type ModelViewerSceneLayerGroupId =
  | 'webgl'
  | 'wall'
  | 'lighting'
  | 'floor-css'
  | 'overlay';

export type ModelViewerSceneLayerToggleDefinition = {
  key: string;
  label: string;
  description: string;
  group: ModelViewerSceneLayerGroupId;
  /** Human label for grouped UI */
  groupLabel: string;
  /** Inspector section where detailed controls live */
  detailSection: string;
  defaultEnabled: boolean;
  /** Only relevant when sceneLayerMode is section-backdrop */
  sectionBackdropOnly?: boolean;
  /**
   * Symbols default off unless legacySceneSymbolsEnabled is explicitly true.
   * Other keys default on when undefined.
   */
  optIn?: boolean;
};

export const MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS: Record<ModelViewerSceneLayerGroupId, string> = {
  webgl: '3D scene (WebGL)',
  wall: 'Wall art & symbols',
  lighting: 'Horizon & wall lighting',
  'floor-css': 'Floor spill & fill (CSS)',
  overlay: 'Foreground overlays',
};

export const MODEL_VIEWER_SCENE_LAYER_TOGGLES: ModelViewerSceneLayerToggleDefinition[] = [
  {
    key: 'sectionSceneWebglReflectionEnabled',
    label: 'Reflection plane (mirror)',
    description: 'WebGL floor reflector — the live mirrored reflection under the vase.',
    group: 'webgl',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.webgl,
    detailSection: '3D floor overrides',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionWebglFloorMatteEnabled',
    label: 'Floor matte surface',
    description: 'Soft tinted floor plane beneath the reflector.',
    group: 'webgl',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.webgl,
    detailSection: '3D floor overrides',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionSceneContactShadowEnabled',
    label: 'Contact shadow',
    description: 'Dark contact shadow under the vase base.',
    group: 'webgl',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.webgl,
    detailSection: '3D floor overrides',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionArtEnabled',
    label: 'Wall texture',
    description: 'Plaster / grain wall texture layer behind the scene.',
    group: 'wall',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.wall,
    detailSection: 'Wall art',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionWallDepthEnabled',
    label: 'Wall texture depth shading',
    description: 'Relief shadows and highlights on the wall texture.',
    group: 'wall',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.wall,
    detailSection: 'Wall art',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'legacySceneSymbolsEnabled',
    label: 'Wall symbols',
    description: 'Gold folklore / celestial line-art symbols on the wall.',
    group: 'wall',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.wall,
    detailSection: 'Symbols',
    defaultEnabled: false,
    optIn: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionBacklightEnabled',
    label: 'Horizon bloom & core glow',
    description: 'CSS backlight behind the vase — bloom, core line, and horizon pools.',
    group: 'lighting',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.lighting,
    detailSection: 'Horizon backlight',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionWallLightEnabled',
    label: 'Wall light spill',
    description: 'Asymmetric light wash on the wall (left text protection).',
    group: 'lighting',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.lighting,
    detailSection: 'Wall light',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionSceneBaseMistEnabled',
    label: 'Base mist (vase foot)',
    description: 'Colourway haze where the vase meets the floor.',
    group: 'lighting',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.lighting,
    detailSection: 'Base mist',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionFloorEnvironmentReflectionEnabled',
    label: 'Environment floor reflection',
    description: 'Single floor pickup layer for horizon glow, wall texture, and symbols.',
    group: 'floor-css',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS['floor-css'],
    detailSection: 'Floor art reflection',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionSceneFloorBaseFillEnabled',
    label: 'Floor base fill',
    description: 'Dark gradient fill from the horizon line to the bottom of the screen.',
    group: 'floor-css',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS['floor-css'],
    detailSection: '3D floor overrides',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionFloorSpillHorizonBandEnabled',
    label: 'Horizon floor colour band',
    description: 'Full-width colour wash at the horizon on the floor zone.',
    group: 'floor-css',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS['floor-css'],
    detailSection: 'Horizon backlight',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionColourwayFloorTailEnabled',
    label: 'Colourway floor tail',
    description: 'Extends colourway tint down toward the viewport bottom.',
    group: 'floor-css',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS['floor-css'],
    detailSection: 'Horizon backlight',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionBacklightFloorSheenEnabled',
    label: 'Backlight floor sheen',
    description: 'Specular sheen pool from the horizon backlight onto the floor.',
    group: 'floor-css',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS['floor-css'],
    detailSection: 'Horizon backlight',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionWallLightFloorSpillEnabled',
    label: 'Wall light floor spill',
    description: 'Floor-zone spill from the wall light layer.',
    group: 'floor-css',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS['floor-css'],
    detailSection: 'Wall light',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionFloorArtReflectionEnabled',
    label: 'Wall art floor reflection',
    description: 'Mirrored wall texture visible on the floor.',
    group: 'floor-css',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS['floor-css'],
    detailSection: 'Wall art',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
  {
    key: 'sectionReflectionForegroundVeilEnabled',
    label: 'Reflection foreground veil',
    description: 'Soft veil over the lower reflection zone.',
    group: 'overlay',
    groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.overlay,
    detailSection: 'Wall art',
    defaultEnabled: true,
    sectionBackdropOnly: true,
  },
];

export function readSceneLayerToggle(
  content: Record<string, unknown>,
  key: string,
  defaultEnabled = true,
  optIn = false
): boolean {
  const value = content[key];
  if (value === undefined) {
    return optIn ? false : defaultEnabled;
  }
  return value === true;
}

export function resolveSceneLayerToggleEnabled(
  content: Record<string, unknown>,
  definition: ModelViewerSceneLayerToggleDefinition
): boolean {
  return readSceneLayerToggle(
    content,
    definition.key,
    definition.defaultEnabled,
    definition.optIn
  );
}

/** Composition pipeline toggle shown alongside scene layers (Position → Advanced). */
export const MODEL_VIEWER_COMPOSITION_LAYER_TOGGLE = {
  key: 'reflectionCloneEnabled',
  label: 'Reflection clone (debug)',
  description: 'Duplicate vase mesh for reflection experiments. Usually off on production pages.',
  group: 'webgl' as ModelViewerSceneLayerGroupId,
  groupLabel: MODEL_VIEWER_SCENE_LAYER_GROUP_LABELS.webgl,
  detailSection: 'Position → Advanced',
};

export function applySectionWebglLayerToggles(
  content: Record<string, unknown>,
  floorDefaults: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  const next = floorDefaults ? { ...floorDefaults } : {};

  if (!readSceneLayerToggle(content, 'sectionSceneWebglReflectionEnabled')) {
    next.reflectionEnabled = false;
    next.reflectorSurfaceOpacity = 0;
  }
  if (!readSceneLayerToggle(content, 'sectionWebglFloorMatteEnabled')) {
    next.floorSurfaceOpacity = 0;
  }
  if (!readSceneLayerToggle(content, 'sectionSceneContactShadowEnabled')) {
    next.showContactShadow = false;
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

const SECTION_FLOOR_PIPELINE_KEYS = [
  'floorShape',
  'floorPlaneMode',
  'floorReflectivity',
  'floorEdgeSoftness',
  'reflectionOpacity',
  'reflectionVisibility',
  'reflectionPlaneYOffset',
  'floorSurfacePlaneYOffset',
  'floorPlaneForwardOffset',
  'reflectionHorizonScreenShift',
  'reflectionForwardOffsetMultiplier',
  'reflectionWidthMultiplier',
  'reflectionDepthMultiplier',
  'reflectionForegroundFade',
  'reflectionDepthFade',
  'reflectionNearBaseSharpness',
  'reflectionViewerSideSoftness',
  'reflectionResolution',
  'floorSurfaceOpacity',
  'reflectorSurfaceOpacity',
  'floorColor',
  'showContactShadow',
  'shadowOpacity',
  'shadowRadiusMultiplier',
  'shadowPlaneEpsilon',
] as const;

/** Merge explicit section floor defaults with legacy keys still stored on publicPipelineOverrides. */
export function resolveSectionFloorPipelineDefaults(
  content: Record<string, unknown>
): Record<string, unknown> | undefined {
  const explicit =
    content.sectionFloorPipelineDefaults &&
    typeof content.sectionFloorPipelineDefaults === 'object' &&
    !Array.isArray(content.sectionFloorPipelineDefaults)
      ? (content.sectionFloorPipelineDefaults as Record<string, unknown>)
      : {};
  const pipeline =
    content.publicPipelineOverrides &&
    typeof content.publicPipelineOverrides === 'object' &&
    !Array.isArray(content.publicPipelineOverrides)
      ? (content.publicPipelineOverrides as Record<string, unknown>)
      : {};
  const lifted = SECTION_FLOOR_PIPELINE_KEYS.reduce<Record<string, unknown>>((next, key) => {
    if (pipeline[key] !== undefined && explicit[key] === undefined) {
      next[key] = pipeline[key];
    }
    return next;
  }, {});

  const merged = { ...lifted, ...explicit };
  return Object.keys(merged).length > 0 ? merged : undefined;
}

export const SECTION_FLOOR_SPILL_LAYER_TOGGLE_KEYS: Record<string, string> = {
  'environment-floor-reflection': 'sectionFloorEnvironmentReflectionEnabled',
  'horizon-floor-band': 'sectionFloorSpillHorizonBandEnabled',
  'colourway-floor-tail': 'sectionColourwayFloorTailEnabled',
  'horizon-floor-sheen': 'sectionBacklightFloorSheenEnabled',
  'wall-light-floor-spill': 'sectionWallLightFloorSpillEnabled',
  'wall-floor-reflection': 'sectionFloorArtReflectionEnabled',
  'symbol-floor-reflection': 'sectionFloorArtReflectionEnabled',
};
